import { Injectable } from "@angular/core";
import { catchError, Observable, of, ReplaySubject, tap } from "rxjs";
import { environment } from "@env/environment";
import { HttpClient } from "@angular/common/http";
import { Zone, ZoneRequest } from "./zone.interface";

@Injectable()
export class ZoneService {

    baseUrl = environment.administration_url + '/admin/zones';
    private _zone: ReplaySubject<Zone> = new ReplaySubject<Zone>(1);
    private _zones: ReplaySubject<Zone[]> = new ReplaySubject<Zone[]>(1);

    set zone(value: Zone) {
        this._zone.next(value);
    }

    get zone$() {
        return this._zone.asObservable();
    }

    set zones(value: Zone[]) {
        this._zones.next(value);
    }

    get zones$() {
        return this._zones.asObservable();
    }

    constructor(
        private _httpClient: HttpClient
    ) { }

    getAll(): Observable<Zone[]> {
        return this._httpClient.get<Zone[]>(`${this.baseUrl}`)
        .pipe(
            tap((response: Zone[]) => {
                this.zones = response || [];
                return response;
            }),
            catchError(() => of([]))
        );
    }

    getById(uuid: string): Observable<Zone> {
        return this._httpClient.get<Zone>(`${this.baseUrl}/${uuid}`)
        .pipe(
            tap((zone) => {
                this.zone = zone;
                return zone;
            }),
            catchError(() => of({} as Zone))
        );
    }

    getByRegionUuid(regionUuid: string): Observable<Zone[]> {
        return this._httpClient.get<Zone[]>(`${this.baseUrl}/by-region/${regionUuid}`)
        .pipe(
            tap((response: Zone[]) => {
                this.zones = response || [];
                return response;
            }),
            catchError(() => of([]))
        );
    }

    create(data: ZoneRequest): Observable<Zone> {
        return this._httpClient.post<Zone>(`${this.baseUrl}`, data)
        .pipe(
            tap((zone) => {
                this.zone = zone;
                return zone;
            }),
            catchError(() => of({} as Zone))
        );
    }

    update(uuid: string, data: ZoneRequest): Observable<Zone> {
        return this._httpClient.put<Zone>(`${this.baseUrl}/${uuid}`, data)
        .pipe(
            tap((zone) => {
                this.zone = zone;
                return zone;
            }),
            catchError(() => of({} as Zone))
        );
    }

    delete(uuid: string): Observable<void> {
        return this._httpClient.delete<void>(`${this.baseUrl}/${uuid}`)
        .pipe(
            catchError(() => of(undefined))
        );
    }
}
